/**
 * Participant task card for the calibration arm (Study 0) and, later, the main
 * verification study. Facts and boundaries only — no advice about how to check.
 * Source: docs/POLICY_CARD_DRAFT_2026-11-01.md (v2) + the seven additions in
 * docs/ITEM_BANK_V3_DRAFT_2026-11-01.md §1. Bump CARD_REVISION when this changes.
 */
export const CARD_REVISION = 'card-v2.1-2026-11-01';

export interface CardSection {
  title: string;
  intro?: string;
  items: string[]; // **bold** markers are rendered as <strong>
}

export const POLICY_CARD: CardSection[] = [
  {
    title: 'About you',
    items: [
      'You are a first-year student in the College of Business.',
      'You also work **10 hours a week as a student assistant in the Dean\'s Office**.',
      'You use your university email for classes and for the job.',
    ],
  },
  {
    title: 'Your systems',
    intro: 'All university systems are at **university.edu** or a name ending in **.university.edu**.',
    items: [
      '**University email** — classes and the job — mail.university.edu',
      '**Student Portal** — registration, tuition, financial aid, and your **direct deposit** for refunds and student pay. Only you change your own details there. — portal.university.edu',
      '**Course site** — assignments, grades, course files — courses.university.edu',
      '**Campus Drive** — your files and the Dean\'s Office shared folder — drive.university.edu',
      '**Sign-in app** on your phone — any time someone tries to log in with your university username, the app sends you a request: *"Sign-in attempt from [device] in [city]. Approve?"* Approving it completes that login.',
      '**Dean\'s Office purchasing system** — you enter supply orders; your supervisor, **Dana Ortiz**, approves them and releases payment — purchasing.university.edu',
      '**IT Help Desk** — problems, questions, reporting suspicious messages — help.university.edu · 970-555-0100',
    ],
  },
  {
    title: 'What you do in the job, and what you may do',
    items: [
      'You **enter** supply orders in the purchasing system. **Only Dana can approve an order or release a payment**; your account can only enter.',
      'Orders go only to suppliers on the **approved supplier list** shown in the purchasing system — currently **Front Range Office Supply** only, on a standing monthly order. Suppliers are added by the **University Business Office**; no one in the Dean\'s Office, including Dana, can add one.',
      'You submit **your own** timesheet every other Friday. Dana approves timesheets. You never submit or approve anyone else\'s.',
      'You and the other student assistant, **Maya Chen**, work in the Dean\'s Office folder on Campus Drive. **Files in that folder can be shared only with university accounts.** If someone outside the university needs a file, Dana shares it herself.',
      '**Only Dana** may hold delegate access to anything of yours, and only to your **work calendar**, for scheduling. The Dean\'s Office does not allow student assistants to delegate to other staff. The office staff are Dana, Maya and you.',
      'Dana works in the office Monday to Thursday; her desk phone is 970-555-0120.',
    ],
  },
  {
    title: 'Your account',
    items: [
      'Your direct deposit in the Student Portal goes to your checking account **ending in 2208**. It is the only account on file.',
      'On **Monday** you activated the **Sign-in app on your new iPhone**. The app said you would get a message asking you to confirm the new device.',
      'On **Monday** you also installed the **university\'s official mobile app (University Mobile)**. The install screen said the app would ask to connect to your account and would read your schedule and grades.',
      'You have **not** signed up for any other app, tool, or service with your university account this term.',
      'You live in **Parmelee Hall, room 214**.',
    ],
  },
  {
    title: 'What you did this week',
    items: [
      '**Monday 9:10 am:** logged in to the Student Portal from your laptop and changed your **phone number** (new phone). You did not change anything else. You renewed your **housing for Spring, same room**. You have requested no room change.',
      '**Tuesday:** entered this month\'s supply order for Front Range Office Supply (order #4471), as usual. Dana approved it Tuesday afternoon. Dana also asked you to install the **University VPN** from software.university.edu this week so you can enter orders from home. You have opened no IT ticket this term. You added **BUS 160** to your Spring schedule in the Student Portal. You have dropped nothing.',
      '**Wednesday:** shared "Fall Events Budget.xlsx" with Maya on Campus Drive for event planning.',
      '**Thursday 2:05 pm:** logged in to the course site from your laptop, in the library, to submit the BUS 150 assignment.',
      '**Friday 9:00 am:** requested a password reset from your laptop (the new phone had logged you out). You have made no other reset request.',
      'You are logging in **only from your own laptop and phone**, in Fort Collins.',
    ],
  },
  {
    title: 'People you work with',
    items: [
      '**Dana Ortiz** — your supervisor, Dean\'s Office',
      '**Maya Chen** — the other student assistant',
      '**Prof. Lee** — your BUS 150 instructor',
      '**IT Help Desk** — help.university.edu, 970-555-0100',
    ],
  },
];
